import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { SolfilmShowcase } from "@/components/sections/SolfilmShowcase";
import { BbnComparisonTables } from "@/components/sections/BbnComparisonTables";
import { EnergyCalculator } from "@/components/sections/EnergyCalculator";
import { SolfilmFullCatalog } from "@/components/sections/SolfilmFullCatalog";
import { CallbackRail } from "@/components/CallbackRail";
import { TechDetails } from "@/components/TechDetails";
import { SpecComparisonTable } from "@/components/SpecComparisonTable";
import { IMAGES, BBN_PHOTOS } from "@/lib/images";

export const FAQS: FaqItem[] = [
  {
    q: "Hur mycket sänker solfilm temperaturen inomhus?",
    a: "I södervända kontorsmiljöer mäter vi typiskt en sänkning på 3–6 °C under sommarens topptimmar efter att solfilm monterats. Den exakta effekten beror på glasets U- och G-värde, fönstrets riktning och vilken filmserie som används. 3M Prestige-serien reducerar 36–61 % av solens värmeenergi (TSER enligt datablad 1173225O) utan att förmörka rummet; för högre värden finns exteriör silverfilm.",
  },
  {
    q: "Hur snabbt betalar solfilmen tillbaka sig?",
    a: "För kontors- och butiksfastigheter med komfortkyla är typisk återbetalningstid 3–5 år genom minskat kylbehov och lägre energiförbrukning. Vid kraftigt soliga fasader och fullt kylda lokaler kan återbetalningen vara snabbare. I rådgivningen räknar vi alltid igenom besparingen utifrån din fastighets faktiska driftdata.",
  },
  {
    q: "Mörknar rummet när vi sätter upp solfilm?",
    a: "Inte med moderna spektralt selektiva filmer. 3M Prestige 70 släpper in 68 % av det synliga ljuset samtidigt som värmestrålning och UV blockeras. Vi visar gärna provbitar på plats så att ni kan se skillnaden innan ni bestämmer er.",
  },
  {
    q: "Kan vi sätta solfilm på alla typer av glas?",
    a: "Nästan alla. Det går att montera solfilm på enkelglas, vanliga 2-glas och energiglas. För vissa isolerglaskombinationer (t.ex. moderna lågemissionsglas i sydläge) rekommenderar vi utvändig film för att undvika termisk stress. Vi gör alltid en glasanalys innan installation så att garantin för glaset bibehålls.",
  },
  {
    q: "Påverkar solfilmen glasgarantin?",
    a: "När rätt filmtyp väljs för glaset gör den inte det. Vi följer 3M:s installationsspecifikation samt glastillverkarens anvisningar och dokumenterar val av film, vilket gör att både glas- och filmgaranti gäller. Felaktigt vald film på fel glastyp kan däremot orsaka termisk spricka. Därför kontrollerar vi alltid glaset först.",
  },
  {
    q: "Hur lång är installationstiden för ett kontor?",
    a: "En genomsnittlig kontorsvåning på 300–500 m² fönsteryta installeras på 1–2 arbetsdagar med 2 montörer. Vi planerar monteringen tillsammans med er så att den stör så lite som möjligt.",
  },
  {
    q: "Vad ingår i 10 års garanti?",
    a: "Vi lämnar 10 års produktgaranti på filmen (avflagning, missfärgning, sprickbildning) och 10 års arbetsgaranti på installationen (bubblor, kanter, vidhäftning). 3M Prestige har dessutom egen tillverkargaranti från 3M.",
  },
  {
    q: "Servar ni hela Skåne och Göteborg?",
    a: "Ja. Vi har vår bas i Staffanstorp och utgår därifrån till hela Skåne, bland annat Malmö, Lund, Helsingborg, Kristianstad och Ystad, samt Göteborg med kringliggande kommuner. Resa och etablering ingår i offerten utan extra påslag inom dessa områden.",
  },
  {
    q: "Vilka problem löser fönsterfilm i en kommersiell fastighet?",
    a: "Sex återkommande problem: (1) för högt värmeinsläpp från solen genom fasad­fönster och ovanljus, (2) bländande och reflekterande strålar mot skärmar, (3) för kraftigt och skarpt ljusinsläpp, (4) blekning av inventarier, utställda varor, möbler och textil, (5) inbrott, vandalism och splitterrisk, och (6) brist på avskärmning/insynsskydd. Solfilm hjälper mot de fyra första problemen. För de övriga kan vi kombinera den med säkerhetsfilm eller dekorfilm.",
  },
  {
    q: "Vilken effekt kan vi förvänta oss av utvändig 3M-film?",
    a: "Med utvändig solfilm på rutan kan man uppnå total värmeavvisning på upp till 85 %. Ett av våra största utvändiga referensprojekt är Kista Science Tower i Stockholm, där 3M Prestige 40 Exterior är monterad på kontorsfasaden för att sänka värmeinsläpp och kylbehov utan att förmörka arbetsmiljön.",
  },
  {
    q: "Skiljer sig 3M och Solar Screen åt i praktiken?",
    a: "Båda är professionella tillverkare. 3M är amerikanskt, har bredare forskning och utveckling och täcker hela premiumsegmentet med bland annat metallfria Prestige. Solar Screen finns i Luxemburg och erbjuder filmer i mellansegmentet med lägre kvadratmeterpris. För typiska kontors- och butiksinstallationer är skillnaden i upplevd effekt liten. Vi väljer märke utifrån glastyp, budget och projekt­storlek.",
  },
  {
    q: "Vad är garantin på Solar Screen?",
    a: "Solar Screen lämnar 10 års produktgaranti på invändiga filmer och 5–8 år på utvändiga. Spegelfilmerna Silver 285XC och 250XC är de enda på marknaden med 10 års tillverkargaranti utvändigt. Behöver filmen bytas på garantin kontaktar ni oss, inte tillverkaren.",
  },
  {
    q: "Vad innebär invändig kontra utvändig montering?",
    a: "Invändig film monteras med renvattenmetoden på glasets insida. Där är den skyddad från väder och har därför 50–100 % längre livslängd. Utvändig film används när glasets konstruktion (moderna 3-glas lågemissionsglas, takfönster, atriumglas) eller invändiga skuggor (persienner i glaspaketet) gör invändig montering olämplig. Vi gör alltid en glasanalys innan vi föreslår film. I 90 % av fallen monteras filmen invändigt.",
  },
  {
    q: "Hur går rådgivningen till — innan vi behöver besluta något?",
    a: "Fyra steg, alla kostnadsfria: (1) temperaturmätning vid era fönster under en vecka för att dokumentera värmeinfallet, (2) energibesparingsberäkning för projekt över 500 m², (3) simulering av filmeffekt i våra beräkningsverktyg, och (4) termisk stresstest av glaset där det finns minsta risk för termisk spräckning.",
  },
];


export const Route = createFileRoute("/solfilm-foretag/")({
  head: () => ({
    meta: [
      { title: "Solfilm företag & fastighet — auktoriserad 3M-installatör | Offert | BBN Nordic" },
      { name: "description", content: "Solfilm för företag, fastighet, kontor, butik, skolor, industribyggnad och vårdfastighet. Auktoriserad 3M- och Solar Screen-installatör sedan 2008 — energibesparande solfilm på glasfasad, upphandling, entreprenad och projekt. Kostnadsfri offert i Skåne, Göteborg och Stockholm, 10 års garanti." },
      { name: "keywords", content: "solfilm företag, solfilm fastighet, solfilm kontor, solfilm projekt, solfilm installatör, solfilm entreprenad, solfilm upphandling, solfilm offert, solfilm glasfasad, energibesparande solfilm, auktoriserad solfilm installatör, fönsterfilm företag, fönsterfilm fastighet, fönsterfilm kontor, solfilm för fastigheter, solfilm för skolor, solfilm för kommersiella fastigheter, solfilm fastighetsförvaltare, solfilm fastighetsägare, solskyddsfilm, solskydd fastighet, solskydd kontor" },
      { property: "og:title", content: "Solfilm företag & fastighet — 3M-auktoriserad" },
      { property: "og:description", content: "Solfilm för företag, fastighet, kontor, butik och skolor — 3M Prestige, Solar Screen, invändig och utvändig. Offert, projekt, entreprenad och upphandling." },
      { property: "og:url", content: "https://bbnnordic.se/solfilm-foretag" },
      { property: "og:image", content: IMAGES.solfilmHero },
      { property: "og:type", content: "website" },

    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/solfilm-foretag" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Installation av solfilm för företag och fastigheter",
          serviceType: "Solfilms-installation",
          provider: { "@type": "LocalBusiness", name: "BBN Nordic AB", url: "/" },
          areaServed: [
            { "@type": "AdministrativeArea", name: "Skåne" },
            { "@type": "City", name: "Göteborg" },
          ],
          image: IMAGES.solfilmHero,
          url: "/solfilm-foretag",
          brand: { "@type": "Brand", name: "3M" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Solfilm för företag och fastigheter",
          category: "Fönsterfilm / Solfilm",
          description: "Professionell 3M och Solar Screen solfilm för kontor, butiker, skolor och fastigheter i Skåne. Sänker kylbehov, minskar bländning och skyddar inredning.",
          image: IMAGES.solfilmHero,
          brand: { "@type": "Brand", name: "3M" },
          manufacturer: { "@type": "Organization", name: "3M" },
          url: "/solfilm-foretag",
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "10000",
            priceSpecification: { "@type": "PriceSpecification", price: "10000", priceCurrency: "SEK" },
            availability: "https://schema.org/InStock",
            seller: { "@id": "/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
          review: [
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Andrée Johnsson" },
              reviewBody: "Kan varmt rekommenderas. Vi hade en undermålig solfilm som konkurrent satt upp, Marcus knackade på när han var hos grannen och sa att det där kan göras bättre.",
            },
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Christian Backe" },
              reviewBody: "Vi hade problem med att våra fönster släppte in väldigt mycket värme och valde en film som BBN satte med bästa resultat. Kan varmt rekommendera!",
            },
          ],
        }),

      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd(FAQS)),
      },
    ],
  }),
  component: () => (
    <ContentPage
      disableCta
      landingConversion
      eyebrow="SOLFILM — FÖRETAG"
      title="Solfilm för företag och fastigheter"
      subtitle="Med solfilm kan ni minska kylbehov och bländning och skydda inredningen. Ni behåller utsikten och behöver varken byta glas eller bygga om."
      heroImage={BBN_PHOTOS.buildingSun}
      breadcrumbs={[{ label: "Solfilm företag" }]}
      sections={[
        {
          eyebrow: "VAD NI FÅR",
          heading: "Så påverkar solfilm lokaler och kylkostnader.",
          paragraphs: [
            "Solfilmen tar bort upp till tre fjärdedelar av solens värmeenergi vid glaset, innan den når rummet. I utsatta zoner mäter vi en temperatursänkning på 3–6 °C. Det blir mindre bländning på skärmarna och kylanläggningen behöver inte gå för fullt under värmeböljan.",
            "Ni får lägre driftkostnad och en arbetsmiljö som fungerar bättre även i juli, med högre produktivitet. Solfilmen monteras på befintligt glas, så ni slipper glasbyte och ombyggnad och behåller utsikten.",
          ],
          bullets: [
            "3–6 °C svalare i södervända kontor under sommarens topptimmar",
            "Återbetalningstid 3–5 år för fastigheter med komfortkyla",
            "Färre klagomål från personal och mindre bländning på skärmar även mitt på dagen",
            "Inventarier, golv och exponerade varor skyddade från UV-blekning",
            "10 års produktgaranti och installation av certifierade 3M-montörer",
          ],
          facts: [
            { value: "50 %", label: "Solvärme bort (TSER)", source: "3M Prestige 70" },
            { value: "99 %", label: "UV-blockering", source: "3M Prestige datablad" },
            { value: "68 %", label: "Synligt ljus släpps in", source: "3M Prestige 70 datablad" },
          ],
          extra: (
            <div className="space-y-6">
              <SpecComparisonTable
                tone="light"
                caption="3M Prestige 70 — VLT, TSER, UV-blockering och bländningsreduktion enligt 3M:s datablad 1173225O."
                featureHeader="Egenskap"
                columns={[
                  { id: "p70", label: "Prestige 70", highlight: true },
                ]}
                rows={[
                  {
                    feature: "Synligt ljus släpps in (VLT)",
                    values: { p70: "68 %" },
                    source: "3M datablad 1173225O",
                    sourceHref: "/datablad",
                  },
                  {
                    feature: "Solvärme avvisad (TSER)",
                    values: { p70: "50 %" },
                    source: "3M datablad 1173225O",
                    sourceHref: "/datablad",
                  },
                  {
                    feature: "UV-blockering",
                    values: { p70: "99 %" },
                    source: "3M datablad 1173225O",
                  },
                  {
                    feature: "Bländningsreduktion",
                    values: { p70: "23 %" },
                    source: "3M datablad 1173225O",
                  },
                  {
                    feature: "Bäst för",
                    values: {
                      p70: "Maximal utsikt och dagsljus",
                    },
                  },
                ]}
              />
              <TechDetails
                summary="Läs mer om tekniken bakom 3M Prestige"
                sources={[
                  { label: "Se alla datablad", href: "/datablad" },
                  { label: "3M Sun Control / Total Solar Energy Rejected (TSER) — definition", href: "https://www.3m.com/3M/en_US/p/c/films/window/" },
                ]}
              >
                <p>
                  3M Prestige bygger på nanoteknik. Hundratals mikrotunna polymerlager
                  lamineras till ett spektralt selektivt filter. Det reflekterar bort
                  infraröd strålning (värmen) men släpper igenom synligt ljus. Eftersom
                  filmen inte innehåller någon metall förändras inte fasadens utseende,
                  mobilsignal och radiovågor passerar fritt, och filmen korroderar inte
                  över tid.
                </p>
                <p>
                  Total Solar Energy Rejected (TSER) är den standardiserade mätstickan
                  för solfilm. Värdet anger andelen av solens totala energi (UV + synligt
                  ljus + infraröd värme) som inte passerar glaset. För högre TSER än
                  Prestige-serien används exteriör silverfilm.
                </p>
              </TechDetails>
            </div>
          ),
          image: IMAGES.solfilm,
        },
        {
          eyebrow: "VAR DET GÖR STÖRST SKILLNAD",
          heading: "Var gör solfilm störst skillnad?",
          paragraphs: [
            "Vi har installerat solfilm i över ett decennium åt fastighetsägare, butikskedjor och offentlig verksamhet i Skåne och Göteborg. Ofta är problemen desamma: stora glaspartier mot söder eller väster, klagomål från dem som använder lokalerna och kylsystem som inte räcker till. Solfilmen minskar värmeinsläppet utan att vi behöver ändra fasaden.",
          ],
          bullets: [
            "Kontor & open space — bländfria skärmar, jämn arbetstemperatur",
            "Butiker & shoppingcenter — exponerade varor skyddade, svalare entré",
            "Skolor & utbildningslokaler — koncentration i klassrum mot söder",
            "Sjukhus & vårdmiljöer — bättre komfort för patienter vid fönster",
            "Industri & lager — svalare pickområden och produktion",
            "Bilhallar & showroom — färre reflexer, skyddade produkter",
            "Restauranger & hotell — gästkomfort utan att skugga utsikten",
            "Fastighetsbolag — energieffektivisering av beståndet utan glasbyte",
          ],
          image: IMAGES.installer,
        },
        {
          eyebrow: "VÅR PROCESS",
          heading: "Så går ett solfilmsprojekt till.",
          paragraphs: [
            "Sedan 2008 har vi arbetat med fastighetsägare och driftansvariga. Ni får fast pris och en namngiven projektledare, och vi planerar jobbet efter hur er fastighet används.",
          ],
          bullets: [
            "1. Kostnadsfri rådgivning via telefon eller videomöte",
            "2. Platsbesök — glasanalys, filmval, dokumentation",
            "3. Skriftlig offert med fast pris och specificerade garantier",
            "4. Tidsbokning anpassad efter er verksamhet",
            "5. Installation av certifierade 3M-montörer enligt 3M:s spec",
            "6. Slutbesiktning och digitalt installationsprotokoll",
          ],
          image: IMAGES.solfilmHero,
        },
        {
          eyebrow: "VÅRT SORTIMENT",
          heading: "Solfilm från 3M och Solar Screen.",
          paragraphs: [
            "Vi är auktoriserade installatörer av både 3M (USA) och Solar Screen (Luxemburg). Vi jämför filmerna utifrån ert glas, er hotbild och er budget.",
          ],
          bullets: [
            "3M Prestige (invändig), nanoteknik utan metall",
            "3M utvändig solfilm — för isolerglas där invändigt är olämpligt",
            "Solar Screen — kostnadseffektivt europeiskt alternativ",
            "Klar säkerhetsfilm — kombinera solskydd med splitterskydd",
          ],
          image: IMAGES.solfilm,
        },
      ]}
      related={[
        { to: "/3m-prestige", label: "3M Prestige-serien", desc: "Nanoteknik utan metall och 15 års garanti." },
        { to: "/3m-sakerhetsfilm-med-solfilm", label: "Kombifilm — säkerhet + solfilm", desc: "Solskydd och säkerhet i samma film för butik och kontor." },
        { to: "/3m-sakerhetsfilm-utan-solfilm", label: "Klar säkerhetsfilm", desc: "När fasadens uttryck ska bevaras helt." },
        { to: "/3m-ultra-sakerhetsfilm", label: "3M Ultra säkerhetsfilm", desc: "Säkerhetsfilm mot inbrott och splitter." },
        { to: "/solfilm-privat", label: "Solfilm för privatpersoner", desc: "Villor, bostadsrätter och fritidshus." },
        { to: "/auktoriserad-installator", label: "Auktoriserad installatör", desc: "Så verifierar du certifiering och garanti." },
        { to: "/datablad", label: "Tekniska datablad", desc: "PDF-datablad för hela 3M- och Solar Screen-sortimentet." },
        { to: "/referenser", label: "Referensprojekt", desc: "Se våra installationer, bland annat Kista Science Tower." },
        { to: "/om-fonsterfilm", label: "Om fönsterfilm", desc: "Läs om filmtyper, insynsskydd och livslängd." },
      ]}
      priorityContent={
        <>
          <EnergyCalculator variant="section" contactHref="/offert" />
          <SolfilmShowcase profile="foretag" />
          <BbnComparisonTables />
          <SolfilmFullCatalog />
        </>
      }
    >
      <FaqSection
        heading="Frågor vi får från fastighetsägare och driftansvariga."
        intro="Funderar ni på solfilm för en kontorsfastighet, butik eller skola? Här är de vanligaste frågorna vi får från projektledare, fastighetschefer och driftansvariga i Skåne och Göteborg."
        items={FAQS}
      />
      <CallbackRail product="Solfilm" />
    </ContentPage>
  ),
});
